import { useMutation, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@copro/api";
import { useSpace } from "../../../hooks/useSpace";
import { keys } from "./keys";

async function deleteSubjectAction(id: string) {
  // Fetch documents and space_id before cascade delete removes them
  const { data: action } = await apiClient
    .from('subject_actions')
    .select('sub_topics!inner(topics!inner(space_id)), documents(file_url)')
    .eq('id', id)
    .single();

  const spaceId = action?.sub_topics?.topics?.space_id;
  const driveUrls = action?.documents
    ?.map(doc => doc.file_url)
    .filter(url => url.includes('drive.google.com')) ?? [];

  // Delete files from Google Drive
  if (driveUrls.length > 0 && spaceId) {
    await apiClient.functions.invoke('delete-from-drive', {
      body: { fileUrls: driveUrls, spaceId },
    });
  }

  // Delete action (cascade deletes documents from DB)
  const { error } = await apiClient.from("subject_actions")
    .delete()
    .eq("id", id);

  if (error) {
    throw error;
  }
}

type Variables = {
  id: string;
  subTopicId: string;
}

export function useDeleteSubjectAction() {
  const queryClient = useQueryClient();
  const { currentSpace } = useSpace();

  return useMutation({
    mutationFn: async ({ id }: Variables) => deleteSubjectAction(id),
    onSuccess: (_, { subTopicId }) => {
      queryClient.invalidateQueries({ queryKey: keys.subTopic(subTopicId) });
      queryClient.invalidateQueries({ queryKey: [...keys.all, 'activityTimeline'] });
      queryClient.invalidateQueries({ queryKey: [...keys.all, 'communications'] });
      queryClient.invalidateQueries({ queryKey: ['communications_count'] });
      queryClient.invalidateQueries({ queryKey: keys.pendingSubTopics(currentSpace!.id) });
    }
  });
}
